import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Fire, Tag } from '@phosphor-icons/react';
import { getEffectivePrice, hasProPrice, getPricePerKg } from './shopUtils';

export const ProductCard = ({ p, showCategory = true, compact = false, colors, categories, getLink, isInWishlist, toggleWishlist, store, addItem, storeSlug, isPro }) => {
  const catName = useMemo(() => {
    if (!showCategory) return null;
    const cat = categories.find(c => c.name === p.category || c.id === p.category);
    return cat?.name || p.category || '';
  }, [p, showCategory, categories]);
  const isPromo = p.is_promo && p.sale_price;
  const isOutOfStock = p.stock_status === 'outofstock';
  const pricePerKg = getPricePerKg(p);
  const effectivePrice = getEffectivePrice(p, isPro);
  const isProPrice = hasProPrice(p, isPro);
  const priceValid = Number.isFinite(effectivePrice);

  return (
    <Link to={getLink(`/product/${p.slug}`)}
      className={`group block bg-white border border-slate-100 hover:shadow-xl transition-all rounded-xl overflow-hidden`}
      data-testid={`product-card-${p.id}`}>
      <div className={`${compact ? 'aspect-[4/3]' : 'aspect-square'} overflow-hidden bg-slate-50 relative`} data-name={p.name?.[0] || '?'}>
        {(p.featured_image || p.images?.[0]) ? (
          <img src={p.featured_image || p.images[0]} alt={p.name}
            className={`w-full h-full object-contain p-3 group-hover:scale-110 transition-transform duration-500 ${isOutOfStock ? 'opacity-50 grayscale-[40%]' : ''}`} loading="lazy"
            onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.classList.add('product-img-fallback'); }} />
        ) : null}
        {!(p.featured_image || p.images?.[0]) && (
          <div className="w-full h-full flex items-center justify-center bg-slate-100">
            <span className="text-3xl font-black text-slate-200">{p.name?.[0]}</span>
          </div>
        )}
        {isPromo && (
          <span className="absolute top-2.5 left-2.5 px-3 py-1.5 text-white text-[12px] font-black rounded-full shadow-md"
            style={{ backgroundColor: colors.primary }}>
            {p.promo_label || `-${Math.round((1 - p.sale_price / p.price) * 100)}%`}
          </span>
        )}
        {p.is_featured && !isPromo && (
          <span className="absolute top-2.5 left-2.5 px-2.5 py-1.5 text-white text-[11px] font-bold rounded-full bg-amber-500 flex items-center gap-1 shadow-md">
            <Fire size={13} weight="fill" /> Best
          </span>
        )}
        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="bg-black/70 text-white text-[12px] font-bold px-4 py-2 rounded-lg uppercase tracking-wide">Rupture de stock</span>
          </div>
        )}
        <button className={`absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center transition-all shadow-sm ${isInWishlist(p.id) ? 'opacity-100 text-pink-500' : 'text-slate-400 hover:text-pink-500 opacity-0 group-hover:opacity-100'}`}
          onClick={e => { e.preventDefault(); e.stopPropagation(); if (store?.id) toggleWishlist(store.id, p.id); }}
          data-testid={`wishlist-toggle-${p.id}`}>
          <Heart size={17} weight={isInWishlist(p.id) ? 'fill' : 'regular'} />
        </button>
      </div>
      <div className={compact ? 'p-3' : 'p-4'}>
        {catName && !compact && (
          <span className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full mb-2 inline-block"
            style={{ backgroundColor: colors.primary + '15', color: colors.primary }}>
            {catName}
          </span>
        )}
        <h3 className={`${compact ? 'text-[13px]' : 'text-[14px]'} font-semibold text-slate-800 line-clamp-2 mb-2 min-h-[40px]`}>{p.name}</h3>
        <div className="mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${compact ? 'text-[16px]' : 'text-[18px]'} font-black`} style={{ color: isProPrice ? '#7c3aed' : colors.primary }}>
              {priceValid ? `${effectivePrice.toFixed(2)} €` : 'Prix sur demande'}
            </span>
            {priceValid && (isPromo || isProPrice) && Number.isFinite(parseFloat(p.price)) && <span className="text-[13px] text-slate-400 line-through">{parseFloat(p.price).toFixed(2)} €</span>}
            {isProPrice && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-100 text-purple-700" data-testid={`pro-badge-${p.id}`}>Pro</span>
            )}
            {priceValid && <small className="text-[11px] text-slate-400 font-normal">TTC</small>}
          </div>
          <div className="text-[11px] text-slate-400">
            {(() => {
              if (!priceValid) return null;
              const rate = typeof p.tax_rate === 'number' ? p.tax_rate : 5.5;
              return <>{(effectivePrice / (1 + rate / 100)).toFixed(2)} € HT <span className="text-slate-300">| TVA {String(rate).replace('.', ',')}%</span></>;
            })()}
          </div>
          {pricePerKg && <div className="text-[10px] font-semibold mt-0.5" style={{ color: colors.primary }}>{pricePerKg} €/kg</div>}
          {(() => {
            const ppLaterEnabled = !!store?.settings?.paypal_pay_later_enabled;
            const minAmt = parseFloat(store?.settings?.paypal_pay_later_min || 600);
            if (!priceValid || !ppLaterEnabled || effectivePrice < minAmt) return null;
            const monthly = (effectivePrice / 4).toFixed(2).replace('.', ',');
            return (
              <div className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded bg-[#FFC439]/25 border border-[#FFC439]/40 text-[10px] font-semibold text-[#003087]" data-testid={`paylater-card-badge-${p.id}`}>
                <span className="font-black">Pay<span className="italic">Pal</span></span>
                <span>4× sans frais · {monthly} €/mois</span>
              </div>
            );
          })()}
          {p.volume_discounts?.length > 0 && (
            <div className="text-[10px] text-amber-600 font-semibold mt-0.5" data-testid={`volume-hint-${p.id}`}>
              Des {p.volume_discounts[0]?.min_qty} pcs : {parseFloat(p.volume_discounts[0]?.price).toFixed(2)} €/u
            </div>
          )}
        </div>
        {isPromo && p.promo_quantity_limit && (
          <p className="text-[11px] text-orange-600 font-semibold mb-2">Plus que {p.promo_quantity_limit} en stock !</p>
        )}
        {!compact && (
          <button onClick={e => { if (!priceValid) return; e.preventDefault(); if (!isOutOfStock) addItem({ ...p, storeSlug }); }}
            className={`w-full py-2.5 text-[13px] font-bold text-white rounded-xl transition-all ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-110 active:scale-[0.97]'}`}
            style={{ backgroundColor: isOutOfStock ? '#94a3b8' : colors.primary }}
            data-testid={`add-to-cart-${p.id}`}>
            {isOutOfStock ? 'Indisponible' : priceValid ? 'Ajouter au panier' : 'Voir le produit'}
          </button>
        )}
      </div>
    </Link>
  );
};

export const ProductRow = ({ p, colors, getLink, addItem, storeSlug, isPro, store }) => {
  const isOutOfStock = p.stock_status === 'outofstock';
  const isPromo = p.is_promo && p.sale_price;
  const pricePerKg = getPricePerKg(p);
  const effectivePrice = getEffectivePrice(p, isPro);
  const isProPrice = hasProPrice(p, isPro);
  const priceValid = Number.isFinite(effectivePrice);

  return (
    <Link to={getLink(`/product/${p.slug}`)}
      className="flex items-center gap-4 bg-white border border-slate-100 hover:shadow-lg transition-all rounded-xl overflow-hidden p-3 group"
      data-testid={`product-row-${p.id}`}>
      <div className="w-[90px] h-[90px] flex-shrink-0 bg-slate-50 rounded-lg overflow-hidden">
        {(p.featured_image || p.images?.[0]) ? (
          <img src={p.featured_image || p.images[0]} alt={p.name} className={`w-full h-full object-contain p-1.5 group-hover:scale-110 transition-transform ${isOutOfStock ? 'opacity-50 grayscale-[40%]' : ''}`} loading="lazy"
            onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><span className="text-xl font-black text-slate-200">{p.name?.[0]}</span></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[14px] font-semibold text-slate-800 line-clamp-1">{p.name}</h3>
        {p.category && <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: colors.primary }}>{p.category}</span>}
        <div className="mt-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[17px] font-black" style={{ color: isProPrice ? '#7c3aed' : colors.primary }}>{priceValid ? `${effectivePrice.toFixed(2)} €` : 'Prix sur demande'}</span>
            {priceValid && (isPromo || isProPrice) && Number.isFinite(parseFloat(p.price)) && <span className="text-[12px] text-slate-400 line-through">{parseFloat(p.price).toFixed(2)} €</span>}
            {isProPrice && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Pro</span>}
            {priceValid && <small className="text-[11px] text-slate-400">TTC</small>}
            {isOutOfStock && <span className="text-[11px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">Rupture</span>}
          </div>
          <span className="text-[11px] text-slate-400">
            {(() => {
              if (!priceValid) return null;
              const rate = typeof p.tax_rate === 'number' ? p.tax_rate : 5.5;
              return <>{(effectivePrice / (1 + rate / 100)).toFixed(2)} € HT <span className="text-slate-300">| TVA {String(rate).replace('.', ',')}%</span></>;
            })()}
          </span>
          {pricePerKg && <span className="text-[10px] font-semibold block" style={{ color: colors.primary }}>{pricePerKg} €/kg</span>}
          {(() => {
            const ppLaterEnabled = !!store?.settings?.paypal_pay_later_enabled;
            const minAmt = parseFloat(store?.settings?.paypal_pay_later_min || 600);
            if (!priceValid || !ppLaterEnabled || effectivePrice < minAmt) return null;
            const monthly = (effectivePrice / 4).toFixed(2).replace('.', ',');
            return (
              <div className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-[#FFC439]/25 border border-[#FFC439]/40 text-[10px] font-semibold text-[#003087]">
                <span className="font-black">Pay<span className="italic">Pal</span></span>
                <span>4× — {monthly} €/mois</span>
              </div>
            );
          })()}
        </div>
      </div>
      <button onClick={e => { if (!priceValid) return; e.preventDefault(); if (!isOutOfStock) addItem({ ...p, storeSlug }); }}
        className={`flex-shrink-0 px-4 py-2 text-[12px] font-bold text-white rounded-xl ${isOutOfStock ? 'opacity-40 cursor-not-allowed' : 'hover:brightness-110'}`}
        style={{ backgroundColor: isOutOfStock ? '#94a3b8' : colors.primary }}>
        {isOutOfStock ? 'Indisponible' : priceValid ? 'Ajouter' : 'Voir'}
      </button>
    </Link>
  );
};
